import { useState } from 'react';
import {
  Text, Paper, Stack, Group, Table, Button, Modal,
  TextInput, Badge, Alert, Skeleton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconFileCheck, IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import dayjs from 'dayjs';
import {
  useExemptionCertificates,
  useExpiringCertificates,
  useUploadExemptionCert,
} from '@/hooks/usePortalApi';

function certStatusBadge(isVerified: boolean, isActive: boolean) {
  if (!isActive) {
    return <Badge color="red" variant="light" size="sm">Expired</Badge>;
  }
  if (isVerified) {
    return <Badge color="green" variant="light" size="sm">Verified</Badge>;
  }
  return <Badge color="yellow" variant="light" size="sm">Pending</Badge>;
}

export default function TaxCertificates() {
  const { data: certs, isLoading: certsLoading } = useExemptionCertificates();
  const { data: expiringCerts } = useExpiringCertificates();
  const uploadCert = useUploadExemptionCert();

  const [modalOpen, setModalOpen] = useState(false);
  const [certNumber, setCertNumber] = useState('');
  const [state, setState] = useState('');
  const [issueDate, setIssueDate] = useState<Date | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [businessType, setBusinessType] = useState('');

  const resetForm = () => {
    setCertNumber('');
    setState('');
    setIssueDate(null);
    setExpiryDate(null);
    setBusinessType('');
  };

  const handleUpload = () => {
    if (!certNumber || !state || !issueDate) {
      notifications.show({
        title: 'Validation Error',
        message: 'Certificate number, state, and issue date are required.',
        color: 'red',
      });
      return;
    }
    uploadCert.mutate(
      {
        cert_number: certNumber,
        state,
        issue_date: dayjs(issueDate).format('YYYY-MM-DD'),
        expiry_date: expiryDate ? dayjs(expiryDate).format('YYYY-MM-DD') : undefined,
        business_type: businessType || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Certificate Uploaded',
            message: 'Your tax exemption certificate has been submitted for verification.',
            color: 'green',
          });
          setModalOpen(false);
          resetForm();
        },
        onError: () => {
          notifications.show({
            title: 'Error',
            message: 'Failed to upload certificate. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const certList = Array.isArray(certs) ? certs : (certs as any)?.items ?? [];
  const expiringList = Array.isArray(expiringCerts) ? expiringCerts : (expiringCerts as any)?.items ?? [];
  const expiringWithin60 = expiringList.filter(
    (c: any) => c.days_until_expiry != null && c.days_until_expiry <= 60,
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Tax Certificates"
        description="Manage your tax exemption certificates"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setModalOpen(true)}
          >
            Upload New Certificate
          </Button>
        }
      />

      {expiringWithin60.length > 0 && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Certificates Expiring Soon"
          color="orange"
          variant="light"
        >
          <Text size="sm">
            The following certificates are expiring within 60 days:{' '}
            {expiringWithin60.map((c: any) => (
              <Text key={c.id} component="span" fw={600}>
                {c.cert_number} ({c.state}
                {c.days_until_expiry != null ? ` - ${c.days_until_expiry} days` : ''})
                {c !== expiringWithin60[expiringWithin60.length - 1] ? ', ' : ''}
              </Text>
            ))}
          </Text>
        </Alert>
      )}

      {certsLoading ? (
        <Stack gap="xs">
          {[...Array(4)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}
        </Stack>
      ) : certList.length === 0 ? (
        <EmptyState
          icon={<IconFileCheck size={30} />}
          title="No certificates"
          description="You have not uploaded any tax exemption certificates yet."
          action={
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setModalOpen(true)}
            >
              Upload New Certificate
            </Button>
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Certificate #</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Issue Date</Table.Th>
                <Table.Th>Expiry Date</Table.Th>
                <Table.Th>Business Type</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {certList.map((cert: any) => (
                <Table.Tr key={cert.id}>
                  <Table.Td>
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {cert.cert_number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{cert.state}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {dayjs(cert.issue_date).format('MMM D, YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {cert.expiry_date
                        ? dayjs(cert.expiry_date).format('MMM D, YYYY')
                        : '--'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{cert.business_type ?? '--'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {certStatusBadge(cert.is_verified, cert.is_active)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          <Text fw={700} size="lg">
            Upload New Certificate
          </Text>
        }
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Certificate Number"
            placeholder="Enter certificate number"
            value={certNumber}
            onChange={(e) => setCertNumber(e.currentTarget.value)}
            required
          />
          <TextInput
            label="State"
            placeholder="e.g. CA, NY, TX"
            value={state}
            onChange={(e) => setState(e.currentTarget.value)}
            required
          />
          <DatePickerInput
            label="Issue Date"
            placeholder="Select issue date"
            value={issueDate}
            onChange={setIssueDate}
            required
          />
          <DatePickerInput
            label="Expiry Date"
            placeholder="Select expiry date (optional)"
            value={expiryDate}
            onChange={setExpiryDate}
            clearable
          />
          <TextInput
            label="Business Type"
            placeholder="e.g. Wholesale, Manufacturing (optional)"
            value={businessType}
            onChange={(e) => setBusinessType(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              loading={uploadCert.isPending}
            >
              Upload Certificate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
